import { FC, ReactNode } from 'react';
import { keyframes } from '@emotion/react';
import { alpha, Box, IconButton, Typography, useTheme } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { observer } from 'mobx-react-lite';

import { useTrans } from '@libs/i18n';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  ClockIcon,
  type Icon as PhosphorIcon,
  QuestionIcon,
  SparkleIcon,
  WarningIcon,
  XCircleIcon,
} from '@phosphor-icons/react';

import { type RunView, STAGE_LABEL_KEYS, type StageSegment } from '../../utils/run-progress';

/**
 * Shared visual pieces for the assessment run screen.
 *
 * The keyframes are the ones remediation's run screen already ships rather than
 * the design's near-identical `k-pulse` / `k-ring`: two sets of almost-the-same
 * animation on two screens of the same product is how a product starts to look
 * assembled rather than designed.
 */

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.4; }
  50%      { transform: scale(1.4); opacity: 1; }
`;

const ring = keyframes`
  0%   { transform: scale(1);    opacity: 0.45; }
  100% { transform: scale(1.25); opacity: 0; }
`;

/** Slides the buffer stripes — motion without a claimed percentage. */
const drift = keyframes`
  from { background-position: 0 0; }
  to   { background-position: 17px 0; }
`;

/**
 * The hatched fill used for in-flight work, everywhere it appears.
 *
 * Deliberately not a solid colour: solid reads as "done", and this is the one
 * part of the bar that is explicitly NOT done. The stripes carry the difference
 * without needing a legend to be read first.
 */
export function inFlightFill(theme: Theme): string {
  return `repeating-linear-gradient(135deg, ${theme.palette.ocean[500]} 0 3px, ${theme.palette.ocean[200]} 3px 7px)`;
}

/** The dashed empty track that means "no data", never "zero". */
export function noDataFill(theme: Theme): string {
  return `repeating-linear-gradient(135deg, ${theme.palette.grey[300]} 0 6px, transparent 6px 12px)`;
}

/**
 * The empty track for a run that is starting up rather than silent.
 *
 * The same stripes as `noDataFill`, in the working colour instead of grey. It
 * is still an empty track, because nothing has been counted — the colour and
 * its drift say "waiting" where the grey says "we cannot see this", which is
 * the only difference there is to draw.
 */
export function pendingFill(theme: Theme): string {
  return `repeating-linear-gradient(135deg, ${alpha(theme.palette.ocean[300], 0.55)} 0 6px, transparent 6px 12px)`;
}

// =============================================================================
// Hero
// =============================================================================

interface MedallionLook {
  icon: PhosphorIcon;
  weight: 'regular' | 'fill';
  bg: string;
  border: string;
  color: string;
  working: boolean;
}

function medallionFor(view: RunView, theme: Theme): MedallionLook {
  switch (view) {
    case 'done':
      return {
        icon: CheckCircleIcon,
        weight: 'fill',
        bg: alpha(theme.palette.accent.green, 0.12),
        border: alpha(theme.palette.accent.green, 0.35),
        color: theme.palette.accent.green,
        working: false,
      };
    case 'runFailed':
      return {
        icon: XCircleIcon,
        weight: 'fill',
        bg: alpha(theme.palette.error.main, 0.1),
        border: alpha(theme.palette.error.main, 0.3),
        color: theme.palette.error.main,
        working: false,
      };
    case 'stalled':
      return {
        icon: WarningIcon,
        weight: 'regular',
        bg: theme.palette.yellow[200],
        border: theme.palette.yellow[400],
        color: theme.palette.yellow[800],
        working: false,
      };
    case 'unknown':
      return {
        icon: QuestionIcon,
        weight: 'regular',
        bg: theme.palette.grey[200],
        border: theme.palette.grey[300],
        color: theme.palette.text.secondary,
        working: false,
      };
    case 'queued':
      return {
        icon: ClockIcon,
        weight: 'regular',
        bg: theme.palette.grey[200],
        border: theme.palette.grey[300],
        color: theme.palette.text.secondary,
        working: false,
      };
    // Starting up gets the WORKING look — ocean, and the pulse ring — because
    // the run is genuinely working: the Job has been dispatched and is booting.
    // The clock face is what separates it from a run already reporting.
    case 'starting':
      return {
        icon: ClockIcon,
        weight: 'regular',
        bg: theme.palette.ocean[100],
        border: theme.palette.ocean[300],
        color: theme.palette.ocean[700],
        working: true,
      };
    default:
      return {
        icon: SparkleIcon,
        weight: 'regular',
        bg: theme.palette.ocean[100],
        border: theme.palette.ocean[300],
        color: theme.palette.ocean[700],
        working: true,
      };
  }
}

export const RunHero: FC<{
  view: RunView;
  title: string;
  body: string;
  /** Elapsed + last-updated pills, and any primary action. */
  trailing?: ReactNode;
  /** Leaves the run. Rendered inside the hero, ahead of the medallion. */
  onBack?: () => void;
  /** Rendered under the stage bar area — the honesty callouts. */
  children?: ReactNode;
}> = observer(({ view, title, body, trailing, onBack, children }) => {
  const theme = useTheme();
  const { t, i18n } = useTrans();
  const isRTL = i18n.dir() === 'rtl';
  const look = medallionFor(view, theme);
  const Icon = look.icon;

  return (
    <Box
      sx={{
        px: { xs: 2, sm: 3 },
        pt: 3,
        pb: 3.5,
        borderBottom: `1px solid ${theme.palette.grey[300]}`,
        background: look.working
          ? `linear-gradient(180deg, ${alpha(theme.palette.ocean[100], 0.5)} 0%, transparent 100%)`
          : 'transparent',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 3,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2.25, flex: 1, minWidth: 300 }}>
          {/* Back sits inside the hero, sharing its gradient — the placement
            remediation's run screen already uses. Given a strip of its own it
            would open a band of bare white above the header. */}
          {onBack && (
            <IconButton
              onClick={onBack}
              aria-label={t('app.assessment.progress.back')}
              sx={{
                width: 34,
                height: 34,
                mt: 0.75,
                flexShrink: 0,
                backgroundColor: alpha(theme.palette.common.white, 0.7),
                border: `1px solid ${theme.palette.grey[300]}`,
                '&:hover': { backgroundColor: theme.palette.common.white },
              }}
            >
              <ArrowLeftIcon size={17} style={{ transform: isRTL ? 'scaleX(-1)' : 'none' }} />
            </IconButton>
          )}
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 4,
              flexShrink: 0,
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: look.bg,
              border: `1px solid ${look.border}`,
              color: look.color,
            }}
          >
            <Icon size={26} weight={look.weight} />
            {look.working && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: -3,
                  borderRadius: 4.5,
                  border: `2px solid ${theme.palette.ocean[700]}`,
                  animation: `${ring} 1.6s ease-out infinite`,
                }}
              />
            )}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                flexWrap: 'wrap',
                mb: 0.5,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600, textWrap: 'balance' }}>
                {title}
              </Typography>
              {look.working && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: theme.palette.ocean[700],
                    fontSize: 11.5,
                    fontWeight: 600,
                  }}
                >
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      backgroundColor: theme.palette.ocean[700],
                      animation: `${pulse} 1.2s ease-in-out infinite`,
                    }}
                  />
                  {t('app.assessment.progress.aiAtWork')}
                </Box>
              )}
            </Box>
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', maxWidth: 640, lineHeight: 1.55 }}
            >
              {body}
            </Typography>
          </Box>
        </Box>

        {trailing && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              flexWrap: 'wrap',
              flexShrink: 0,
            }}
          >
            {trailing}
          </Box>
        )}
      </Box>

      {children}
    </Box>
  );
});

/** One of the hero's rounded stat pills. */
export const HeroPill: FC<{
  icon: PhosphorIcon;
  label: string;
  value: string;
  tone?: 'neutral' | 'warning';
}> = observer(({ icon: Icon, label, value, tone = 'neutral' }) => {
  const theme = useTheme();
  const warn = tone === 'warning';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: 1.75,
        py: 1.25,
        borderRadius: 6,
        border: `1px solid ${warn ? theme.palette.yellow[400] : theme.palette.grey[300]}`,
        backgroundColor: 'background.paper',
      }}
    >
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: 2,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: warn ? theme.palette.yellow[200] : theme.palette.ocean[100],
          color: warn ? theme.palette.yellow[800] : theme.palette.ocean[700],
        }}
      >
        <Icon size={15} />
      </Box>
      <Box>
        <Typography
          sx={{
            fontSize: 9.5,
            fontWeight: 700,
            letterSpacing: '0.07em',
            textTransform: 'uppercase',
            color: 'text.tertiary',
            lineHeight: 1.4,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            color: warn ? theme.palette.yellow[900] : 'text.primary',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
});

// =============================================================================
// Stage bar
// =============================================================================

/**
 * Five segments, sized by weight, each filled by its own measured fraction.
 *
 * There is no overall percentage here and there is not meant to be. Reading
 * documents, matching them and assessing them are counted in different units,
 * and the only way to produce a single number across them is to invent one.
 */
export const StageBar: FC<{
  segments: StageSegment[];
  /** Caption per segment, e.g. "4 of 17 · 3 in flight". */
  captionFor: (segment: StageSegment) => string;
  /** Sits opposite the current stage name. */
  countedLabel?: string;
  currentStageLabel?: string;
}> = observer(({ segments, captionFor, countedLabel, currentStageLabel }) => {
  const theme = useTheme();
  const { t } = useTrans();

  return (
    <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {(currentStageLabel || countedLabel) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
            {currentStageLabel}
          </Typography>
          <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', fontWeight: 500 }}>
            {countedLabel}
          </Typography>
        </Box>
      )}

      {/*
        Stage columns, and the three rules that keep two of them from printing
        on top of each other.

        Scoring and reporting weigh 5% each, so the column that owns "Preparing
        the report" is around 40px wide on a window that is not maximised —
        narrower than the word itself. Left alone the label spills sideways into
        its neighbour and the two 11px labels print on top of each other.

        The floor is `min-content`: a column may not shrink below its longest
        word, so the two light stages take a few pixels more than their weight
        on a narrow window and grading gives them up. Proportion bends before
        legibility does — and only the segment WIDTHS bend. What fills them is
        still measured. `break-word` is the backstop for the width where even
        one word will not fit, and below `sm` the row stops competing for
        horizontal space altogether and stacks.
      */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1.5, sm: '4px' },
          alignItems: 'stretch',
        }}
      >
        {segments.map((segment) => (
          <Box
            key={segment.key}
            sx={{
              flex: { xs: '0 0 auto', sm: `${segment.weight} 1 0%` },
              minWidth: { xs: 0, sm: 'min-content' },
              display: 'flex',
              flexDirection: 'column',
              gap: 0.875,
            }}
          >
            <Box
              sx={{
                height: 10,
                borderRadius: 9999,
                backgroundColor: theme.palette.grey[300],
                overflow: 'hidden',
                display: 'flex',
              }}
            >
              <Box
                sx={{
                  width: `${segment.done}%`,
                  backgroundColor: theme.palette.accent.green,
                  transition: 'width 400ms ease',
                }}
              />
              <Box
                sx={{
                  width: `${segment.inFlight}%`,
                  background: inFlightFill(theme),
                  backgroundSize: '17px 100%',
                  animation: `${drift} 1.1s linear infinite`,
                  transition: 'width 400ms ease',
                  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                }}
              />
            </Box>
            {/* Stacked under the track while there is room for a column; on one
              line, name against count, once the row has stacked. */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'row', sm: 'column' },
                alignItems: { xs: 'baseline', sm: 'stretch' },
                justifyContent: 'space-between',
                gap: { xs: 1.5, sm: 0.875 },
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.6875rem',
                  fontWeight: segment.isCurrent ? 700 : 500,
                  color: segment.isCurrent ? 'text.primary' : 'text.tertiary',
                  lineHeight: 1.35,
                  overflowWrap: 'break-word',
                }}
              >
                {t(STAGE_LABEL_KEYS[segment.key])}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.6875rem',
                  color: 'text.tertiary',
                  lineHeight: 1.35,
                  overflowWrap: 'break-word',
                  textAlign: { xs: 'end', sm: 'start' },
                  whiteSpace: { xs: 'nowrap', sm: 'normal' },
                }}
              >
                {captionFor(segment)}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2.5,
          flexWrap: 'wrap',
          mt: 0.5,
        }}
      >
        <LegendSwatch
          fill={theme.palette.accent.green}
          label={t('app.assessment.progress.counted')}
        />
        <LegendSwatch fill={inFlightFill(theme)} label={t('app.assessment.progress.inFlight')} />
        <LegendSwatch
          fill={theme.palette.grey[300]}
          label={t('app.assessment.progress.notStarted')}
        />
        <Typography
          sx={{
            fontSize: '0.6875rem',
            color: 'text.tertiary',
            marginInlineStart: 'auto',
          }}
        >
          {t('app.assessment.progress.barNote')}
        </Typography>
      </Box>
    </Box>
  );
});

const LegendSwatch: FC<{ fill: string; label: string }> = observer(({ fill, label }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.75,
      fontSize: '0.6875rem',
      color: 'text.secondary',
    }}
  >
    <Box sx={{ width: 14, height: 8, borderRadius: 9999, background: fill }} />
    {label}
  </Box>
));

/**
 * What a run with no reports gets instead of a bar.
 *
 * A dashed, empty track. The whole point is that it cannot be mistaken for a
 * bar at 0% — "we cannot see this run" and "this run has done nothing" are
 * different facts and the old interpolated bar could express neither.
 */
export const NoProgressTrack: FC<{
  /**
   * `nodata` is a blind spot; `pending` is a run that has not started talking
   * yet.
   *
   * The two draw the same empty track — there is genuinely nothing to fill it
   * with either way — but the caption is the difference between telling someone
   * their run is not reporting and telling them it is warming up, and for the
   * first minutes after submitting, only the second is true.
   */
  tone?: 'nodata' | 'pending';
}> = observer(({ tone = 'nodata' }) => {
  const theme = useTheme();
  const { t } = useTrans();
  const pending = tone === 'pending';

  return (
    <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Box
        sx={{
          height: 14,
          borderRadius: 9999,
          border: `1px dashed ${pending ? theme.palette.ocean[300] : theme.palette.grey[600]}`,
          // Pending drifts, so the strip reads as waiting rather than dead. It
          // is the one honest kind of motion here: it carries no percentage.
          background: pending ? pendingFill(theme) : noDataFill(theme),
          ...(pending && { animation: `${drift} 1.1s linear infinite` }),
        }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: '0.8125rem',
            fontWeight: 600,
            color: pending ? theme.palette.ocean[700] : 'text.secondary',
          }}
        >
          {pending ? <ClockIcon size={16} /> : <QuestionIcon size={16} />}
          {t(
            pending
              ? 'app.assessment.progress.startingUp'
              : 'app.assessment.progress.noProgressData',
          )}
        </Box>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.tertiary' }}>
          {t(
            pending
              ? 'app.assessment.progress.startingUpNote'
              : 'app.assessment.progress.noProgressNote',
          )}
        </Typography>
      </Box>
    </Box>
  );
});

/** The "frozen at the last document we counted" strip, for a stalled run. */
export const FrozenNote: FC = observer(() => {
  const theme = useTheme();
  const { t } = useTrans();

  return (
    <Box
      sx={{
        mt: 1.75,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        fontSize: '0.75rem',
        color: theme.palette.yellow[900],
        backgroundColor: theme.palette.yellow[200],
        border: `1px solid ${theme.palette.yellow[400]}`,
        borderRadius: 1,
        px: 1.5,
        py: 1,
      }}
    >
      <WarningIcon size={16} />
      {t('app.assessment.progress.frozenNote')}
    </Box>
  );
});

// =============================================================================
// Counters
// =============================================================================

export interface CounterCell {
  label: string;
  value: string;
  color: string;
}

export const RunCounters: FC<{ counters: CounterCell[] }> = observer(({ counters }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 1.5,
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      }}
    >
      {counters.map((counter) => (
        <Box
          key={counter.label}
          sx={{
            border: `1px solid ${theme.palette.grey[300]}`,
            borderRadius: 2,
            px: 2,
            py: 1.75,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: 11.5,
              fontWeight: 500,
              color: 'text.secondary',
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                flexShrink: 0,
                backgroundColor: counter.color,
              }}
            />
            {counter.label}
          </Typography>
          <Typography
            sx={{
              fontSize: 24,
              fontWeight: 700,
              lineHeight: 1.1,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {counter.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
});
